import { useState, useEffect } from "react";
import { 
  FileText, Search, Filter, ChevronLeft, ChevronRight, User, 
  Shield, Award, Tag, AlertTriangle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionTypeConfig = {
  USER_STATUS_CHANGE: { label: "Cambio Stato Utente", icon: Shield, color: "text-yellow-400" },
  USER_ROLE_CHANGE: { label: "Cambio Ruolo", icon: Shield, color: "text-purple-400" },
  POINTS_ADJUSTMENT: { label: "Modifica Punti", icon: Award, color: "text-blue-400" },
  POINTS_RESET: { label: "Azzeramento Punti", icon: Award, color: "text-red-400" },
  CAMPAIGN_CREATE: { label: "Campagna Creata", icon: Tag, color: "text-green-400" },
  CAMPAIGN_UPDATE: { label: "Campagna Modificata", icon: Tag, color: "text-blue-400" },
  CAMPAIGN_DELETE: { label: "Campagna Eliminata", icon: Tag, color: "text-red-400" }
};

export default function AdminAuditLogs({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("ALL");
  const [targetFilter, setTargetFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchLogs();
  }, [actionFilter, targetFilter, dateFrom, dateTo, page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "30"
      });
      
      if (actionFilter !== "ALL") params.append("action_type", actionFilter);
      if (targetFilter !== "ALL") params.append("target_type", targetFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);

      const response = await authFetch(`${API}/admin/audit-logs?${params}`, { 
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setTotalPages(data.total_pages);
        setTotal(data.total);
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const ActionIcon = ({ actionType }) => {
    const config = actionTypeConfig[actionType];
    const Icon = config?.icon || FileText;
    return <Icon className={`w-4 h-4 ${config?.color || 'text-zinc-400'}`} />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Audit Log</h1>
        <div className="text-zinc-500">{total} eventi registrati</div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center bg-zinc-900/50 p-4 rounded-lg border border-zinc-800">
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48 bg-zinc-950 border-zinc-800">
            <SelectValue placeholder="Tipo Azione" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="ALL">Tutte le azioni</SelectItem>
            <SelectItem value="USER_STATUS_CHANGE">Cambio Stato</SelectItem>
            <SelectItem value="USER_ROLE_CHANGE">Cambio Ruolo</SelectItem>
            <SelectItem value="POINTS_ADJUSTMENT">Modifica Punti</SelectItem>
            <SelectItem value="POINTS_RESET">Azzeramento Punti</SelectItem>
            <SelectItem value="CAMPAIGN_CREATE">Campagna Creata</SelectItem>
            <SelectItem value="CAMPAIGN_UPDATE">Campagna Modificata</SelectItem>
            <SelectItem value="CAMPAIGN_DELETE">Campagna Eliminata</SelectItem>
          </SelectContent>
        </Select>

        <Select value={targetFilter} onValueChange={(v) => { setTargetFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-zinc-950 border-zinc-800">
            <SelectValue placeholder="Target" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="ALL">Tutti</SelectItem>
            <SelectItem value="USER">Utenti</SelectItem>
            <SelectItem value="CAMPAIGN">Campagne</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="bg-zinc-950 border-zinc-800 w-40"
            placeholder="Da"
          />
          <span className="text-zinc-500">-</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="bg-zinc-950 border-zinc-800 w-40"
            placeholder="A"
          />
        </div>

        <Button 
          variant="ghost" 
          onClick={() => {
            setActionFilter("ALL");
            setTargetFilter("ALL");
            setDateFrom("");
            setDateTo("");
            setPage(1);
          }}
        >
          Reset filtri
        </Button>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <div>Nessun evento trovato</div>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log, idx) => (
            <div 
              key={idx}
              className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-4 hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <ActionIcon actionType={log.action_type} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${actionTypeConfig[log.action_type]?.color || 'text-white'}`}>
                        {actionTypeConfig[log.action_type]?.label || log.action_type}
                      </span>
                      <span className="text-zinc-600">•</span>
                      <span className="text-zinc-400 text-sm">{log.target_type}</span>
                    </div>
                    
                    {log.reason && (
                      <div className="text-sm text-zinc-500 mt-1">
                        "{log.reason}"
                      </div>
                    )}
                    
                    {(log.before_json || log.after_json) && (
                      <div className="text-xs mt-2 flex items-center gap-4">
                        {log.before_json && (
                          <span className="text-zinc-500">
                            Prima: <code className="text-red-400">{JSON.stringify(log.before_json)}</code>
                          </span>
                        )}
                        {log.after_json && (
                          <span className="text-zinc-500">
                            Dopo: <code className="text-green-400">{JSON.stringify(log.after_json)}</code>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right shrink-0">
                  <div className="text-sm text-white flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {log.actor_name}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono mt-1">
                    {new Date(log.created_at).toLocaleString("it-IT")}
                  </div>
                  {log.meta_json?.ip && (
                    <div className="text-xs text-zinc-600 font-mono">
                      IP: {log.meta_json.ip}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
          <div className="text-sm text-zinc-500">
            Pagina {page} di {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
